import { Component, Inject } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationBar } from 'src/services/notification.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { environment } from 'src/environment';
import { emsPlayer } from 'src/models/emsModels';
import { ConfirmDialogDialog } from '../confirm-dialog/confirm-dialog.component';
import { nationality, language } from 'src/models/countryModel';
import nationalitiesData from 'src/assets/nationality.json';
import languagesData from 'src/assets/languages.json';

@Component({
  selector: 'app-player-edit',
  templateUrl: './player-edit.component.html',
  styleUrl: './player-edit.component.scss'
})
export class PlayerEditComponent {

  teamID = "";
  workspaceID = "";
  playerID = "";
  notificationBar: NotificationBar;
  playerGroup = new FormGroup({
    nameControl: new FormControl('', [Validators.required]),
    pictureControl: new FormControl('', []),
    nationalityControl: new FormControl('', [Validators.required]),
    languagesControl: new FormControl([], [Validators.required]),
    positionControl: new FormControl(1, [Validators.required, Validators.min(1)]),
    armrestControl: new FormControl(true, [Validators.required]),
    pillowControl: new FormControl(false, [Validators.required]),
    inearsControl: new FormControl(false, [Validators.required]),
    notesControl: new FormControl('', []),    
  });
  isProcessingPictureDelete = false;
  pictureUploadProgress = 0;
  nationalities = Array<nationality>();
  languages = Array<language>();
  placeholderPersonImage = environment.placeholderPersonImage;
  placeholderImage = environment.placeholderImage;
  playerDBRef: any;

  constructor(public dialogRef: MatDialogRef<PlayerEditComponent>, @Inject(MAT_DIALOG_DATA) public data: any, private snackBar: MatSnackBar, private fireStorage: AngularFireStorage, private db: AngularFireDatabase, public dialog: MatDialog) {
    this.notificationBar = new NotificationBar(snackBar);
    this.teamID = data.teamID;
    this.workspaceID = data.workspaceID;
    this.playerID = data.playerID;
  }
  
  ngOnInit() {  
    this.loadCountryInfo();
    this.loadPlayer();
  }

  ngOnDestroy() {
    if (this.playerDBRef) {
      this.playerDBRef.off('value');
    };
  }

  loadCountryInfo() {
    this.nationalities = Object.entries(nationalitiesData).map(([code, name]) => ({ code: code.toLocaleLowerCase(), name })).sort((a, b) => a.name.localeCompare(b.name));
    this.languages = Object.entries(languagesData).map(([code, name]) => ({ code: code.toLocaleUpperCase(), name })).sort((a, b) => a.name.localeCompare(b.name));
  }

  loadPlayer() {
    this.playerDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID);
    this.playerDBRef.on('value', x => {
      var player = x.toJSON() as emsPlayer;
      this.playerGroup.controls.nameControl.setValue(player.name);
      this.playerGroup.controls.pictureControl.setValue(player.picture);
      this.playerGroup.controls.nationalityControl.setValue(player.nationality);
      this.playerGroup.controls.languagesControl.setValue(Object.values(player.languages));
      this.playerGroup.controls.positionControl.setValue(player.position);
      this.playerGroup.controls.armrestControl.setValue(player.armrest);
      this.playerGroup.controls.pillowControl.setValue(player.pillow);
      this.playerGroup.controls.inearsControl.setValue(player.inears);
      this.playerGroup.controls.notesControl.setValue(player.notes);
    });
  }

  changeName() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
      name: this.playerGroup.controls.nameControl.value
    });
  }

  changeNationality() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
      nationality: this.playerGroup.controls.nationalityControl.value
    });
  }

  getSelectedNationality(code: string) {
    return this.nationalities.find(nationality => nationality.code == code);
  }

  changeLanguages() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
      languages: this.playerGroup.controls.languagesControl.value
    });
  }

  changePosition() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
      position: this.playerGroup.controls.positionControl.value
    });
  }

  changeArmrest() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
      armrest: this.playerGroup.controls.armrestControl.value
    });
  }

  changePillow() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
      pillow: this.playerGroup.controls.pillowControl.value
    });
  }

  changeInEars() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
      inears: this.playerGroup.controls.inearsControl.value
    });
  }

  changeNotes() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
      notes: this.playerGroup.controls.notesControl.value
    });
  }

  async uploadPicture(event: any) {
    if (this.playerGroup.controls.pictureControl.value && this.playerGroup.controls.pictureControl.value.length > 0) {
      await this.deletePicture(true);
    }
    var fileType = event.target.files[0].name.split('.').pop();
    fileType = "." + fileType;
    const fileRef = this.fireStorage.ref("/resources/esportsms/w/" + this.workspaceID + "/p/" + `${uuidv4()}` + fileType);
    const task = fileRef.put(event.target.files[0]);
    task.percentageChanges().subscribe((progress) => {
      this.pictureUploadProgress = progress;
    });
    task.snapshotChanges().subscribe({
      complete: () => {
        var fileUrl = "";
        fileRef.getMetadata().subscribe( x => {
          fileUrl = environment.assetDomain + `${x.fullPath.split('.').slice(0,-1).join('.')}_400x400.png`;
        })
        this.pictureUploadProgress = 0;
        setTimeout(() => {
          this.playerGroup.controls.pictureControl.setValue(fileUrl);
          this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
            picture: fileUrl
          });
        }, 1500);
      },
      error: (error) => {
        this.notificationBar.show("Upload failed!")
        this.pictureUploadProgress = 0;
      }
    });
  }

  async deletePicture(skipConfirm: boolean) {
    this.isProcessingPictureDelete = true;
    var res = false;
    if (skipConfirm == false) {
      var dialogRef = this.dialog.open(ConfirmDialogDialog, {
      data: {
        actionType: "delete",
        message: "Are you sure you want to delete the picture?",
      }
    });
    res = await dialogRef.afterClosed().toPromise();
    }
    else {
      res = true;
    }
    if (res == true) {
      var picturePath = new URL(this.playerGroup.controls.pictureControl.value).pathname;
      const fileRef = this.fireStorage.ref(picturePath);
      fileRef.delete();
      this.playerGroup.controls.pictureControl.setValue("");
      this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + this.playerID).update({
        picture: ""
      });
      if (skipConfirm == false) {
        this.notificationBar.show("Picture deleted!");
      }
    }
    this.isProcessingPictureDelete = false;
  }
}
